import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { 
  RefreshCw, CheckCircle2, XCircle, AlertCircle, Activity, Database, 
  Globe, Server, Download, HardDrive, Cpu, Zap
} from 'lucide-react';

const SystemHealthEnhanced = () => {
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastChecked, setLastChecked] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  const fetchHealthData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/health`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        const data = await response.json();
        setHealthData(data);
        setLastChecked(new Date());
      } else if (response.status === 503) {
        const errorData = await response.json();
        setHealthData(errorData);
        setLastChecked(new Date());
      } else {
        setHealthData(null);
      }
    } catch (error) {
      setHealthData({
        status: 'error',
        error: 'Failed to connect to backend',
        database: { status: 'unknown', error: error.message }
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchHealthData(); }, []);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchHealthData, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh]);

  const getStatusBadge = (status) => {
    const isHealthy = ['connected', 'healthy', 'running', 'active', 'configured'].includes(status);
    if (isHealthy) {
      return <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-green-900/30 text-green-400">🟢 {status}</span>;
    } else if (status === 'degraded') {
      return <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-yellow-900/30 text-yellow-400">🟡 {status}</span>;
    }
    return <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-red-900/30 text-red-400">🔴 {status || 'error'}</span>;
  };

  const formatTime = (ts) => ts ? new Date(ts).toLocaleString() : 'Never';

  const exportDiagnostics = () => {
    if (!healthData) return;
    const json = JSON.stringify({ timestamp: new Date().toISOString(), ...healthData }, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `diagnostics-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-3">
      {/* Critical Error Alert */}
      {healthData?.status === 'unhealthy' && healthData?.database?.status === 'failed' && (
        <div className="p-3 bg-red-900/30 border border-red-700 rounded-lg">
          <h3 className="text-xs font-bold text-red-400 mb-1">🚨 CRITICAL: MongoDB Connection Failed</h3>
          <p className="text-[10px] text-red-300">Database connection failing. No data can be saved or retrieved.</p>
          {healthData?.database?.error && (
            <p className="text-[10px] text-red-400 mt-1 font-mono bg-red-900/30 p-1.5 rounded">{healthData.database.error}</p>
          )}
        </div>
      )}

      {/* Header Card - Compact */}
      <Card className="bg-gray-800 border-gray-700">
        <CardContent className="p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-blue-400" />
              <div>
                <h2 className="text-xs font-semibold text-white">System Health Monitor</h2>
                <p className="text-[10px] text-gray-400">Real-time monitoring of system components</p>
              </div>
            </div>
            {healthData && getStatusBadge(healthData.status)}
          </div>
          <div className="flex items-center justify-between">
            <p className="text-[10px] text-gray-400">
              Last Checked: <span className="text-white">{formatTime(lastChecked)}</span>
            </p>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-1.5 text-[10px] text-gray-400 cursor-pointer">
                <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} className="rounded border-gray-600 bg-gray-900 text-blue-600 w-3 h-3" />
                Auto-refresh (30s)
              </label>
              <Button onClick={fetchHealthData} disabled={loading} className="h-6 px-2 text-[10px] bg-blue-600 hover:bg-blue-700">
                <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Deployment & Environment - Compact */}
      {healthData?.deployment && (
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-3">
            <h3 className="text-xs font-semibold text-white mb-2">📦 Deployment Info</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {[
                { label: 'Version', value: healthData.deployment.version },
                { label: 'Environment', value: healthData.deployment.environment },
                { label: 'Last Deploy', value: formatTime(healthData.deployment.deploy_timestamp).split(',')[0] },
                { label: 'Pod', value: healthData.deployment.pod_name?.slice(0, 20) + '...' || 'N/A' },
              ].map(item => (
                <div key={item.label} className="p-2 bg-gray-900 rounded">
                  <p className="text-[10px] uppercase text-gray-500 mb-0.5">{item.label}</p>
                  <p className="text-xs text-white font-medium truncate">{item.value}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Database Stats - Compact */}
      {healthData?.database && (
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-2">
              <Database className="w-3.5 h-3.5 text-purple-400" />
              <h3 className="text-xs font-semibold text-white">Database Statistics</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="p-2 bg-gray-900 rounded">
                <p className="text-[10px] uppercase text-gray-500 mb-0.5">Status</p>
                {getStatusBadge(healthData.database.status)}
              </div>
              <div className="p-2 bg-gray-900 rounded">
                <p className="text-[10px] uppercase text-gray-500 mb-0.5">Response Time</p>
                <p className="text-xs text-white font-medium">{healthData.database.response_time_ms}ms</p>
              </div>
              <div className="p-2 bg-gray-900 rounded">
                <p className="text-[10px] uppercase text-gray-500 mb-0.5">Collections</p>
                <p className="text-xs text-white font-medium">{healthData.database.collections_count || 0}</p>
              </div>
              <div className="p-2 bg-gray-900 rounded">
                <p className="text-[10px] uppercase text-gray-500 mb-0.5">Documents</p>
                <p className="text-xs text-white font-medium">{healthData.database.total_documents?.toLocaleString() || 0}</p>
              </div>
            </div>
            {healthData.database.error && (
              <p className="text-[10px] text-red-400 mt-2 p-2 bg-red-900/20 rounded">{healthData.database.error}</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* API Endpoints - Compact */}
      {healthData?.api_endpoints && (
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-2">
              <Globe className="w-3.5 h-3.5 text-blue-400" />
              <h3 className="text-xs font-semibold text-white">API Endpoints</h3>
            </div>
            <div className="space-y-1">
              {Object.entries(healthData.api_endpoints).map(([key, endpoint]) => (
                <div key={key} className="flex items-center justify-between p-1.5 bg-gray-900 rounded">
                  <code className="text-[10px] text-gray-400">/api/v1/{key.replace(/_/g, '/')}</code>
                  <div className="flex items-center gap-2">
                    {endpoint.response_time_ms && <span className="text-[10px] text-gray-500">{endpoint.response_time_ms}ms</span>}
                    {endpoint.healthy ? (
                      <span className="px-1.5 py-0.5 text-[10px] bg-green-900/30 text-green-400 rounded">{endpoint.status_code}</span>
                    ) : (
                      <span className="px-1.5 py-0.5 text-[10px] bg-red-900/30 text-red-400 rounded">{endpoint.status_code || 'ERR'}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Services Status - Compact */}
      {healthData?.services && (
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-2">
              <Server className="w-3.5 h-3.5 text-green-400" />
              <h3 className="text-xs font-semibold text-white">Services</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {Object.entries(healthData.services).map(([key, service]) => (
                <div key={key} className="p-2 bg-gray-900 rounded">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] text-gray-400 capitalize">{key.replace(/_/g, ' ')}</span>
                    {getStatusBadge(service.status)}
                  </div>
                  <p className="text-[10px] text-gray-500">{service.info}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* System Resources - Compact */}
      {healthData?.system && !healthData.system.error && (
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-2">
              <HardDrive className="w-3.5 h-3.5 text-orange-400" />
              <h3 className="text-xs font-semibold text-white">System Resources</h3>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="p-2 bg-gray-900 rounded">
                <div className="flex items-center gap-1 mb-1">
                  <Cpu className="w-3 h-3 text-blue-400" />
                  <span className="text-[10px] text-gray-400">CPU</span>
                </div>
                <p className="text-sm font-bold text-white">{healthData.system.cpu_percent}%</p>
              </div>
              <div className="p-2 bg-gray-900 rounded">
                <div className="flex items-center gap-1 mb-1">
                  <Zap className="w-3 h-3 text-purple-400" />
                  <span className="text-[10px] text-gray-400">Memory</span>
                </div>
                <p className="text-sm font-bold text-white">{healthData.system.memory_percent}%</p>
                <p className="text-[10px] text-gray-500">{healthData.system.memory_used_gb}GB / {healthData.system.memory_total_gb}GB</p>
              </div>
              <div className="p-2 bg-gray-900 rounded">
                <div className="flex items-center gap-1 mb-1">
                  <HardDrive className="w-3 h-3 text-green-400" />
                  <span className="text-[10px] text-gray-400">Disk</span>
                </div>
                <p className="text-sm font-bold text-white">{healthData.system.disk_percent}%</p>
                <p className="text-[10px] text-gray-500">{healthData.system.disk_used_gb}GB / {healthData.system.disk_total_gb}GB</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions - Compact */}
      <Card className="bg-gray-800 border-gray-700">
        <CardContent className="p-3">
          <h3 className="text-xs font-semibold text-white mb-2">⚡ Quick Actions</h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
            <Button onClick={fetchHealthData} disabled={loading} className="h-7 px-2 text-[10px] bg-gray-900 border border-gray-700 hover:bg-gray-700 text-blue-400">
              🧪 Test Endpoints
            </Button>
            <Button onClick={exportDiagnostics} disabled={!healthData} className="h-7 px-2 text-[10px] bg-gray-900 border border-gray-700 hover:bg-gray-700 text-blue-400">
              <Download className="w-3 h-3 mr-1" /> Export JSON
            </Button>
            <Button disabled className="h-7 px-2 text-[10px] bg-gray-900 border border-gray-700 text-gray-500">
              📋 View Logs
            </Button>
            <Button disabled className="h-7 px-2 text-[10px] bg-red-900/30 border border-red-700 text-red-400">
              🔄 Restart
            </Button>
          </div>
          <p className="text-[10px] text-gray-500 mt-2">Some actions require admin privileges.</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default SystemHealthEnhanced;
